import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { find, groupBy, map, sortBy, trim } from 'lodash';
import memberAPI from '../../middleware/memberAPI';
import { alertError, parseMemberQRCode } from '../../Util/Util';
import productPackageAPI from '../../middleware/productPackageAPI';
import Swal from 'sweetalert2';
import Barcode from 'react-barcode';
import HowToRegRoundedIcon from '@material-ui/icons/HowToRegRounded';
import StarRoundedIcon from '@material-ui/icons/StarRounded';
import PersonRoundedIcon from '@material-ui/icons/PersonRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  PACKAGE_PRODUCT_MEMBER_TYPE,
  PACKAGE_PRODUCT_MEMBER_TYPE_TEXT,
  ProductPackageStatus,
} from '../../data/common';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import RedeemRoundedIcon from '@material-ui/icons/RedeemRounded';
import ThumbUpRoundedIcon from '@material-ui/icons/ThumbUpRounded';
import { useSelector } from 'react-redux';
import bonusAPI from '../../middleware/bonusAPI';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    padding: theme.spacing(2),
  },
  coverImg: {
    width: '150px',
    maxHeight: '200px',
    objectFit: 'cover',
    borderRadius: theme.spacing(1),
  },
  card: {
    margin: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '12px',
  },
  successCard: {
    margin: '12px 0',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '12px',
    color: '#83ad5d',
    fontSize: '2rem',
  },
  name: {
    fontSize: '2rem',
    fontWeight: '600',
  },
  products: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: '6px',
    width: '100%',
  },
  giftIcon: {
    width: '20px',
    verticalAlign: 'bottom',
    color: '#239d77',
    marginLeft: '3px',
  },
  barcodeWrap: {
    '& > svg, & svg': {
      maxWidth: '100%',
      height: 'auto',
      display: 'block',
    },
  },
  noBarcodeMessage: {
    padding: theme.spacing(2, 3),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
    color: '#999',
    fontSize: '0.9rem',
    fontWeight: 500,
    border: '2px dashed #ddd',
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // 警告卡片 - 醒目的紅色警告區塊
  warningCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(238, 90, 111, 0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    border: '2px solid rgba(255, 255, 255, 0.3)',
  },
  warningIcon: {
    color: '#fff',
    fontSize: '28px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    flexShrink: 0,
  },
  warningContent: {
    flex: 1,
  },
  warningTitle: {
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    lineHeight: 1.4,
    textShadow: '0 1px 2px rgba(0,0,0,0.15)',
  },

  // 會員身份徽章容器
  memberTypeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 2),
    borderRadius: '24px',
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    marginTop: theme.spacing(1.5),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    },
  },

  // VIP 會員徽章 - 金色奢華漸層
  memberTypeBadgeVip: {
    background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    color: '#5c3d2e',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    '& svg': {
      filter: 'drop-shadow(0 1px 2px rgba(92, 61, 46, 0.2))',
    },
  },

  // 一般會員徽章 - 簡潔灰階
  memberTypeBadgeGeneral: {
    background: 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
    color: '#495057',
    border: '2px solid rgba(255, 255, 255, 0.8)',
    '& svg': {
      filter: 'drop-shadow(0 1px 2px rgba(73, 80, 87, 0.15))',
    },
  },

  // 徽章圖示
  badgeIcon: {
    fontSize: '18px',
  },

  // 套券購買限制標籤 - 精緻卡片式設計
  packageRestrictionBar: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    boxShadow:
      '0 4px 12px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    },
  },
  restrictionLabel: {
    fontSize: '0.85rem',
    color: '#64748b',
    fontWeight: 600,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
  },
  restrictionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: '24px',
    fontSize: '0.9rem',
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  restrictionBadgeVip: {
    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    color: '#8b4513',
    boxShadow:
      '0 3px 10px rgba(252, 182, 159, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
    border: '1px solid rgba(255, 236, 210, 0.8)',
  },
  restrictionBadgeGeneral: {
    background: 'linear-gradient(135deg, #e0f7fa 0%, #80deea 100%)',
    color: '#00695c',
    boxShadow:
      '0 3px 10px rgba(128, 222, 234, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
    border: '1px solid rgba(224, 247, 250, 0.8)',
  },
  restrictionIcon: {
    fontSize: '18px',
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
  },
}));

const ProductPackageBuy = (props) => {
  const classes = useStyles();
  const { scanData, giftNumber } = props;
  const { currentStore } = useSelector((state) => state.loginState);
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState();
  const [packageId, setPackageId] = useState();
  const [buyCount, setBuyCount] = useState(1);
  const [user, setUser] = useState();
  const [productPackage, setProductPackage] = useState();
  const [pageStep, setPageStep] = useState('check');
  const [cover, setCover] = useState();
  const [groupProducts, setGroupProducts] = useState();

  // 檢查使用者是否可以購買套券（使用 useMemo 快取結果）
  const canUserPurchase = useMemo(() => {
    if (!user || !productPackage) {
      return false;
    }

    // 如果套券限制為一般會員，所有人都可以購買
    if (
      productPackage.memberType === PACKAGE_PRODUCT_MEMBER_TYPE.GENERAL_MEMBER
    ) {
      return true;
    }

    // 如果套券限制為 VIP 會員，只有付費會員可以購買
    if (productPackage.memberType === PACKAGE_PRODUCT_MEMBER_TYPE.VIP_MEMBER) {
      return user.isPayingMember === true;
    }

    return true;
  }, [user, productPackage]);

  useEffect(() => {
    if (trim(scanData) !== '') {
      const qrcodeData = parseMemberQRCode(scanData);

      if (trim(giftNumber) === '') {
        setUserId(qrcodeData.userId);
        setPackageId(qrcodeData.productPackageId);
        setBuyCount(qrcodeData.buyCount || 1);
      } else {
        // 提供給贈品使用 || 所以是掃 會員 QRCODE 進來的
        setIsLoading(true);

        bonusAPI
          .verifyQrcode(qrcodeData.member)
          .then(
            (response) => {
              if (response) {
                setUserId(response.userId);
                setBuyCount(1);
                setPackageId(giftNumber); // 贈品套券 ID
              }
            },
            (error) => {
              alertError(error);
            },
          )
          .then(() => {
            setIsLoading(false);
          });
      }
    } else {
      setUserId(undefined);
      setPackageId(undefined);
      setBuyCount(1);
    }
  }, [scanData, giftNumber]);

  useEffect(() => {
    if (userId) {
      setIsLoading(true);

      memberAPI
        .getMember(userId)
        .then(
          (rs) => {
            setUser(rs);
          },
          (error) => {
            alertError(error);
          },
        )
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [userId]);

  useEffect(() => {
    if (packageId) {
      setIsLoading(true);

      productPackageAPI
        .getProductPackage(packageId)
        .then(
          (rs) => {
            if (!!rs) {
              if (
                rs.storeInfo &&
                rs.storeInfo.id &&
                currentStore &&
                currentStore.id &&
                rs.storeInfo.id === currentStore.id
              ) {
                setProductPackage(rs);
              } else {
                Swal.fire({
                  title: '無法購買不同分店的套券',
                  icon: 'error',
                });
              }
            } else {
              Swal.fire({
                title: '找不到套券資料',
                icon: 'error',
              });
            }
          },
          (error) => {
            alertError(error);
          },
        )
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [packageId, currentStore]);

  useEffect(() => {
    if (productPackage) {
      const findCover = find(
        productPackage.packageProductImageList,
        (image) => image.isCover === true,
      );

      setCover(findCover ? findCover : undefined);

      const newGroupProducts = groupBy(
        sortBy(productPackage.packageProductList),
        (product) => {
          return `${product.isGift ? '1' : '0'}_${product.bonusProductInfo.id}`;
        },
      );

      setGroupProducts(newGroupProducts);
    }
  }, [productPackage]);

  const handleSendProductPackage = () => {
    if (trim(packageId) === '' || trim(userId) === '') {
      Swal.fire({
        title: '無法發送套券',
        icon: 'error',
        text: '缺少套券 ID 或 使用者 ID',
      });

      return false;
    }

    Swal.fire({
      title: '再次確認發送套券',
      icon: 'question',
      showCancelButton: true,
      showConfirmButton: true,
      cancelButtonText: '取消',
      confirmButtonText: '確認',
    }).then((rs) => {
      if (rs.value) {
        setIsLoading(true);

        productPackageAPI
          .sendProductPackageToUser(packageId, userId, buyCount)
          .then(
            () => {
              setPageStep('done');
            },
            (error) => {
              alertError(error);
            },
          )
          .then(() => {
            setIsLoading(false);
          });
      }
    });
  };

  return (
    <div className={classes.root}>
      {pageStep === 'done' && (
        <div className={classes.successCard}>
          <ThumbUpRoundedIcon />
          發送完成
        </div>
      )}

      {pageStep === 'check' && (
        <>
          {productPackage && (
            <div className={classes.card}>
              {cover && (
                <img src={cover.imageUrl} className={classes.coverImg} />
              )}

              <div className={classes.name}>{productPackage.name}</div>

              <div className={classes.barcodeWrap}>
                {productPackage.barcode && (
                  <Barcode
                    height={100}
                    fontSize={16}
                    value={productPackage.barcode}
                    width={3}
                  />
                )}

                {!productPackage.barcode && (
                  <div className={classes.noBarcodeMessage}>無 BARCODE</div>
                )}
              </div>

              <TextField
                label="購買套券數量"
                type="number"
                value={buyCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 0) {
                    setBuyCount(value);
                  } else if (e.target.value === '') {
                    setBuyCount(1);
                  }
                }}
                inputProps={{ min: 1 }}
                variant="outlined"
                fullWidth
                style={{ marginBottom: '16px' }}
              />

              {productPackage.memberType !== undefined && (
                <div className={classes.packageRestrictionBar}>
                  <span className={classes.restrictionLabel}>適用於</span>
                  <div
                    className={`${classes.restrictionBadge} ${
                      productPackage.memberType ===
                      PACKAGE_PRODUCT_MEMBER_TYPE.VIP_MEMBER
                        ? classes.restrictionBadgeVip
                        : classes.restrictionBadgeGeneral
                    }`}>
                    {productPackage.memberType ===
                    PACKAGE_PRODUCT_MEMBER_TYPE.VIP_MEMBER ? (
                      <StarRoundedIcon className={classes.restrictionIcon} />
                    ) : (
                      <PersonRoundedIcon className={classes.restrictionIcon} />
                    )}
                    {productPackage.memberType ===
                    PACKAGE_PRODUCT_MEMBER_TYPE.GENERAL_MEMBER
                      ? '所有會員'
                      : PACKAGE_PRODUCT_MEMBER_TYPE_TEXT[
                          productPackage.memberType
                        ]}
                  </div>
                </div>
              )}

              {groupProducts && (
                <Accordion style={{ width: '100%' }}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography>產品內容</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className={classes.products}>
                      {map(groupProducts, (products, productIndex) => {
                        const product = products[0];

                        return (
                          <div key={productIndex}>
                            {product.bonusProductInfo.name} X {products.length}
                            {product.isGift && (
                              <RedeemRoundedIcon className={classes.giftIcon} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}
            </div>
          )}

          <Divider variant="fullWidth" />

          {user && (
            <div className={classes.card}>
              <div className={classes.name}>{user.realName}</div>
              <div>
                {trim(user.cellPhone) !== '' ? user.cellPhone : user.email}
              </div>
              <div
                className={`${classes.memberTypeBadge} ${
                  user.isPayingMember
                    ? classes.memberTypeBadgeVip
                    : classes.memberTypeBadgeGeneral
                }`}>
                {user.isPayingMember ? (
                  <StarRoundedIcon className={classes.badgeIcon} />
                ) : (
                  <PersonRoundedIcon className={classes.badgeIcon} />
                )}
                {user.isPayingMember ? 'VIP 會員' : '一般會員'}
              </div>
            </div>
          )}

          <Button
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
            startIcon={<HowToRegRoundedIcon />}
            disabled={isLoading || !productPackage || !canUserPurchase}
            onClick={handleSendProductPackage}>
            發送套券
          </Button>

          {!canUserPurchase &&
            user &&
            productPackage &&
            productPackage.memberType ===
              PACKAGE_PRODUCT_MEMBER_TYPE.VIP_MEMBER && (
              <div className={classes.warningCard}>
                <WarningRoundedIcon className={classes.warningIcon} />
                <div className={classes.warningContent}>
                  <div className={classes.warningTitle}>僅限 VIP 會員購買</div>
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default ProductPackageBuy;
